import scriptjs from 'scriptjs';
import constants from '../../../qti/Constants';
import sessionReader from '../../../store/readHelpers/SessionReader';
import { reportError } from '../../../services/ErrorReporting';

const cache = {};
const configOverrides = {
    'messageStyle': 'none',
    'showProcessingMessages': false,
    'CommonHTML': {
        linebreaks: {
            automatic: true,
            width: '90% container',
        },
    },
    'HTML-CSS': {
        linebreaks: {
            automatic: true,
            width: '90% container',
        },
    },
    'SVG': {
        linebreaks: {
            automatic: true,
            width: '90% container',
        },
    },
};

export function writeInlineMathJaxConfigScript() {
    const mathJaxConfig = document.createElement('script');
    mathJaxConfig.type = 'text/x-mathjax-config';
    mathJaxConfig.innerText = 'MathJax.Hub.Config({ skipStartupTypeset: true });';
    document.getElementsByTagName('head')[0].appendChild(mathJaxConfig);
}

let mjIsConfigured = false;

const MathJax2Display = {
    addToCache(element, cacheKey) {
        const isRendered = element.getElementsByClassName('MathJax_CHTML').length > 0
            && element.getElementsByClassName('MJXc-processing').length === 0;
        if (!isRendered) {
            return;
        }

        cache[cacheKey] = Array.from(element.children);
    },

    async getMathJaxInstance() {
        if (window.MathJax) {
            this.configureMathJaxIfNeeded(window.MathJax);
            return window.MathJax;
        }

        let mathJaxUrl;
        if (sessionReader?.isOfflineMode()) {
            mathJaxUrl = `${constants.staticLibPathsOffline.MATHJAX2}MathJax.js?config=TeX-AMS_CHTML`;
        } else if (sessionReader?.isViewMediaPrintOrPdf()) {
            mathJaxUrl = `${constants.staticLibPaths.MATHJAX2}MathJax.js?config=TeX-AMS_SVG`;
        } else {
            mathJaxUrl = `${constants.staticLibPaths.MATHJAX2}MathJax.js?config=TeX-AMS_CHTML`;
        }

        return new Promise((resolve, reject) => {
            scriptjs(mathJaxUrl, () => {
                if (!window.MathJax) {
                    return reject();
                }
                this.configureMathJaxIfNeeded(window.MathJax);
                return resolve(window.MathJax);
            });
        });
    },

    configureMathJaxIfNeeded(MJ) {
        if (!mjIsConfigured) {
            MJ.Hub.Config(configOverrides);
            MJ.Hub.Register.MessageHook('End Math', () => {
                MJ.Extension['TeX/AMSmath'].labels = {};
                MJ.Extension['TeX/AMSmath'].mqlabels = {};
            });
            mjIsConfigured = true;
        }
    },

    async showMathJax2(wrapperElement) {
        // Collect visible mathjax elements except the ones inside ckeditor
        if (!wrapperElement) {
            return false;
        }

        const mathTexElements = Array
            .from(wrapperElement.getElementsByClassName('math-tex'))
            .filter(element => !element.className.includes('cke_widget_element'))
            .map((element) => {
                return {
                    element,
                    shouldCacheElement: !sessionReader.useDynamicLoading() && !element.closest('.skipMathCache'),
                };
            });

        const typeSettingsToDo = [];
        mathTexElements.forEach((elementData, index) => {
            const { element, shouldCacheElement } = elementData;
            const isRendered = element.getElementsByClassName('MathJax_CHTML').length > 0;
            if (isRendered) {
                return;
            }

            const key = `e_${wrapperElement.id}_${wrapperElement.lang}_${index}`;
            const cachedContent = cache[key];
            if (cachedContent && shouldCacheElement) {
                Array.from(element.childNodes)
                    .forEach((node) => {
                        element.removeChild(node);
                    });

                cachedContent.forEach((node) => {
                    element.appendChild(node);
                });

                return;
            }

            element.classList.add('hidden');
            typeSettingsToDo.push(
                this.typesetAndCache(element, key)
            );
        });

        return Promise.all(typeSettingsToDo);
    },

    async typesetAndCache(mathTexElement, cacheKey) {
        const MJ = await this.getMathJaxInstance();

        return new Promise((resolve, reject) => {
            try {
                /* eslint-disable new-cap */
                MJ.Hub.Queue(['Typeset', MJ.Hub, mathTexElement]);
                MJ.Hub.Queue(() => {
                    mathTexElement.classList.remove('hidden');
                });
                MJ.Hub.Queue(() => {
                    this.addToCache(mathTexElement, cacheKey);
                    resolve();
                });
            } catch (error) {
                reportError(error);
                reject(error);
            }
        });
    },
};

export default MathJax2Display;
